import {DatePipe} from "@angular/common";
import {Component, ElementRef, inject, input, ChangeDetectionStrategy, computed} from "@angular/core";
import {toSignal} from "@angular/core/rxjs-interop";
import {MatIcon} from "@angular/material/icon";
import {TranslatePipe, TranslateService} from "@ngx-translate/core";
import {Translations} from "@translations";
import {map, tap, timer} from "rxjs";

@Component({
	selector: "sim-event-calendar-tooltip",
	templateUrl: "./event-calendar-tooltip.component.html",
	styleUrl: "./event-calendar-tooltip.component.scss",
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [DatePipe, MatIcon, TranslatePipe],
})
export class EventCalendarTooltipComponent {
	// Inputs
	protected readonly title = input.required<string>();
	protected readonly targetElement = input.required<HTMLElement>();
	protected readonly date = input.required<{start: Date | string; end?: Date | string}>();

	// Injections
	readonly #elementRef = inject(ElementRef);
	readonly translate = inject(TranslateService);

	// Constants
	protected readonly T = Translations;
	readonly #oneDayInMs = 24 * 60 * 60 * 1000;

	protected readonly isVisible = toSignal(
		timer(150).pipe(
			tap(() => this.#updatePosition()),
			map(() => true),
		),
		{initialValue: false},
	);

	protected readonly difference = computed<number>(() => {
		if (!this.hasEndDate()) {
			return 0;
		}
		return new Date(this.date().end).getTime() - new Date(this.date().start).getTime();
	});

	protected readonly isMoreThanDay = computed<boolean>(() => {
		return this.difference() >= this.#oneDayInMs;
	});

	protected readonly hasEndDate = computed<boolean>(() => {
		return !!this.date().end;
	});

	protected readonly isAllDay = computed<boolean>(() => {
		return this.difference() === this.#oneDayInMs || this.difference() === this.#oneDayInMs - 1;
	});

	#updatePosition(): void {
		const hostEl = this.#elementRef.nativeElement;
		const targetRect = this.targetElement().getBoundingClientRect();
		const {left, top} = this.#calculatePosition(targetRect, hostEl.offsetWidth, hostEl.offsetHeight);

		hostEl.style.left = `${left}px`;
		hostEl.style.top = `${top}px`;
	}

	#calculatePosition(target: DOMRect, hostWidth: number, hostHeight: number): {left: number; top: number} {
		const offset = 8;
		let left = target.right + offset;
		let top = target.top + target.height / 2 - hostHeight / 2;

		const isOverflowing = left + hostWidth > window.innerWidth || top < 0 || top + hostHeight > window.innerHeight;

		if (isOverflowing) {
			const idealLeft = target.left + target.width / 2 - hostWidth / 2;
			const maxLeft = window.innerWidth - hostWidth - offset;

			left = Math.max(offset, Math.min(idealLeft, maxLeft));

			const perfectTop = target.top - hostHeight - offset;
			top = perfectTop < 0 ? target.bottom + offset : perfectTop;
		}

		return {left, top};
	}
}
